/**
 * ESAMessageExpandOverlay — full-canvas reading overlay for long ESA messages.
 *
 * Design: position:absolute inset:10px inside the CanvasOverlayManager's
 * relative container (DR-8), identical to InvokeSolutionOverlay positioning.
 *
 * Singleton: only one message can be expanded at a time. Clicking the expand
 * icon on another message replaces the content in this same overlay rather than
 * opening a second one (managed by the store's expandedMessage field).
 *
 * Escape key and the × close button both call onClose, which clears
 * expandedMessage in esaDomainStore.
 */

import React, { useEffect } from 'react';
import { ESAMessageBubble } from '../../Esa/components/messages/ESAMessageBubble';
import { ESASparkIcon } from '../../Esa/components/panel/ESASparkIcon';
import type { ESACacheMessage } from '../../Esa/cache/MessageCache';

interface ESAMessageExpandOverlayProps {
  message: ESACacheMessage;
  onClose: () => void;
}

export function ESAMessageExpandOverlay({
  message,
  onClose,
}: ESAMessageExpandOverlayProps): React.ReactElement {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div
      data-testid="esa-message-expand-overlay"
      className="absolute inset-[10px] z-[101] rounded-xl shadow-elevation-4 border border-border-default bg-surface flex flex-col overflow-hidden"
    >
      {/* Header */}
      <div className="shrink-0 min-h-[48px] border-b border-border-default flex items-center gap-[10px] px-3.5 bg-surface-secondary">
        <ESASparkIcon size={22} radius={11} />
        <span className="text-[13px] font-semibold text-text-primary">Message</span>
        <div className="flex-1" />
        <button
          type="button"
          data-testid="esa-message-expand-overlay-close"
          title="Close"
          onClick={onClose}
          className="w-[30px] h-[30px] shrink-0 inline-flex items-center justify-center rounded-md cursor-pointer bg-surface border border-border-strong text-text-primary hover:bg-surface-hover transition-colors"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M4 4l8 8M12 4l-8 8" />
          </svg>
        </button>
      </div>

      {/* Body — full message content without bubble chrome */}
      <div className="flex-1 overflow-y-auto p-[20px_24px]">
        <ESAMessageBubble message={message} naked />
      </div>
    </div>
  );
}
